/**
 * Shared styled components and helpers used by the auth form cluster:
 * LoginForm, SetPasswordForm, SignupForm.
 */
import React from 'react';
import {
  Card,
  TextField,
  Alert,
  InputAdornment,
  IconButton,
  alpha,
  styled,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { TextFieldProps } from '@mui/material/TextField';

// ---------------------------------------------------------------------------
// Styled primitives
// ---------------------------------------------------------------------------

/** Card with rounded corners, glass-morphism look, and a green top bar */
export const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 520,
  width: '100%',
  borderRadius: 24,
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: '24px 24px 0 0',
  },
}));

/** Text field with rounded corners and a subtle focus highlight */
export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: alpha(theme.palette.grey[100], 0.5),
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.grey[100], 0.8),
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
}));

// ---------------------------------------------------------------------------
// PasswordField
// ---------------------------------------------------------------------------

export interface PasswordFieldProps extends Omit<TextFieldProps, 'type'> {
  /** Controlled show/hide state */
  showPassword: boolean;
  /** Toggle callback */
  onToggleShow: () => void;
  /** Whether the parent form is loading (disables toggle button) */
  isLoading?: boolean;
  /**
   * Icon order convention:
   *  - 'hide-when-shown'  → VisibilityOff when visible, Visibility when hidden  (LoginForm / SignupForm style)
   *  - 'show-when-shown'  → Visibility when visible, VisibilityOff when hidden  (SetPasswordForm style)
   * Defaults to 'hide-when-shown'.
   */
  iconOrder?: 'hide-when-shown' | 'show-when-shown';
  /** When true, applies the green-tinted hover style on the toggle button (LoginForm / SetPasswordForm) */
  styledToggle?: boolean;
  /** Leading adornment (e.g. Lock icon) */
  startAdornment?: React.ReactNode;
}

/**
 * Password text field with a show/hide visibility toggle button.
 * Renders identical DOM to the inline versions in each form.
 */
export const PasswordField: React.FC<PasswordFieldProps> = ({
  showPassword,
  onToggleShow,
  isLoading = false,
  iconOrder = 'hide-when-shown',
  styledToggle = false,
  startAdornment,
  InputProps,
  ...rest
}) => {
  const VisibleIcon =
    iconOrder === 'hide-when-shown' ? (
      <VisibilityOff sx={{ fontSize: '1.25rem' }} />
    ) : (
      <Visibility sx={{ fontSize: '1.25rem' }} />
    );

  const HiddenIcon =
    iconOrder === 'hide-when-shown' ? (
      <Visibility sx={{ fontSize: '1.25rem' }} />
    ) : (
      <VisibilityOff sx={{ fontSize: '1.25rem' }} />
    );

  const toggleButton = (
    <InputAdornment position="end">
      <IconButton
        onClick={onToggleShow}
        edge="end"
        disabled={isLoading}
        sx={
          styledToggle
            ? {
                '&:hover': {
                  backgroundColor: alpha('#90bf45', 0.08),
                },
              }
            : undefined
        }
      >
        {showPassword ? VisibleIcon : HiddenIcon}
      </IconButton>
    </InputAdornment>
  );

  const mergedInputProps = {
    ...InputProps,
    ...(startAdornment
      ? {
          startAdornment: (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ),
        }
      : {}),
    endAdornment: toggleButton,
  };

  return (
    <StyledTextField
      {...rest}
      type={showPassword ? 'text' : 'password'}
      InputProps={mergedInputProps}
    />
  );
};

// ---------------------------------------------------------------------------
// Shared error Alert
// ---------------------------------------------------------------------------

export interface AuthErrorAlertProps {
  message: string | null | undefined;
}

/**
 * Styled error Alert used by all auth forms.
 * Renders nothing when message is falsy.
 */
export const AuthErrorAlert: React.FC<AuthErrorAlertProps> = ({ message }) => {
  if (!message) return null;
  return (
    <Alert
      severity="error"
      sx={{
        mb: 3,
        borderRadius: 2,
        '& .MuiAlert-icon': {
          fontSize: '1.5rem',
        },
      }}
    >
      {message}
    </Alert>
  );
};

// ---------------------------------------------------------------------------
// Shared handleInputChange factory
// ---------------------------------------------------------------------------

/**
 * Creates a change handler that:
 *  1. Updates the named field in formData state.
 *  2. Clears the matching validation error.
 *  3. Clears the global error via clearError().
 */
export function makeInputChangeHandler<T extends Record<string, any>>(
  field: keyof T,
  setFormData: React.Dispatch<React.SetStateAction<T>>,
  validationErrors: Record<string, string>,
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  error: string | null | undefined,
  clearError: () => void
) {
  return (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field as string]) {
      setValidationErrors(prev => ({
        ...prev,
        [field as string]: '',
      }));
    }

    // Clear global error
    if (error) {
      clearError();
    }
  };
}
